/**
 * Wallet Service
 * Handles wallet balance, transaction history, and Razorpay top-up checkout.
 *
 * @author Varun Pandey
 */

import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import type { WalletResponse, WalletHistoryResponse } from "@/api/types";
import { PaymentService } from "@/services/paymentService";

export interface WalletTopUpCheckoutParams {
  amount: number;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  onSuccess: () => void | Promise<void>;
  onFailure: (error: { code?: string; description?: string }) => void;
}

export class WalletService {
  /**
   * Get wallet balance and top transactions
   */
  static async getBalance(): Promise<WalletResponse> {
    const response = await apiClient.get<WalletResponse>(
      API_ENDPOINTS.WALLET.BALANCE,
    );
    return response.data;
  }

  /**
   * Create recharge order
   */
  static async createOrder(
    amount: number,
  ): Promise<{ success: boolean; data: { order_id: string; amount: number } }> {
    const response = await apiClient.post(API_ENDPOINTS.WALLET.CREATE_ORDER, {
      amount,
    });
    return response.data;
  }

  /**
   * Get full transaction history
   */
  static async getHistory(page: number = 1): Promise<WalletHistoryResponse> {
    const response = await apiClient.get<WalletHistoryResponse>(
      API_ENDPOINTS.WALLET.HISTORY,
      {
        params: { page },
      },
    );
    return response.data;
  }

  /**
   * Verify top-up payment
   */
  static async verifyPayment(paymentId: string): Promise<any> {
    const response = await apiClient.post(API_ENDPOINTS.WALLET.VERIFY_PAYMENT, {
      payment_id: paymentId,
    });
    return response.data;
  }

  /**
   * Create order and open Razorpay checkout (all platforms including iOS).
   */
  static async startTopUpCheckout(params: WalletTopUpCheckoutParams): Promise<void> {
    const order = await WalletService.createOrder(params.amount);

    if (!order?.success || !order.data?.order_id) {
      throw new Error('Failed to create payment order');
    }

    void PaymentService.openCheckout({
      amount: order.data.amount,
      orderId: order.data.order_id,
      name: params.customerName,
      description: `Wallet Recharge: ${params.amount} StarCoins`,
      email: params.customerEmail,
      phone: params.customerPhone,
      onSuccess: async (response) => {
        if (response?.razorpay_payment_id) {
          await WalletService.verifyPayment(response.razorpay_payment_id);
        }
        await params.onSuccess();
      },
      onFailure: params.onFailure,
    });
  }
}
